import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Bookmark as BookmarkIcon, ChevronLeft, ChevronRight, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { clearMark, decodeMark, describeMark, encodeMark, loadMark, saveMark } from "../lib/bookmark";

const atStart = (m) => (m.mode === "pages" ? (m.page || 1) <= 1 : m.mode === "zoom" ? !m.step : !m.item);
import { PublicHeader } from "./Showcase";
import { ScrollReader } from "./ScrollReader";
import { GuidedReader } from "./GuidedReader";

export default function ComicReader() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [n, setN] = useState(1);
  const [mode, setMode] = useState(window.innerWidth < 640 ? "zoom" : "pages");
  const [start, setStart] = useState(null);
  const [offer, setOffer] = useState(null);
  const [pos, setPos] = useState(null);
  const [gen, setGen] = useState(0);
  const apply = useCallback((m) => { setMode(m.mode); if (m.mode === "pages") setN(m.page || 1); setStart(m); setOffer(null); setGen((g) => g + 1); }, []);
  const initial = useRef(loadMark(slug));
  useEffect(() => {
    let live = true;
    api.get(`/public/books/${slug}/comic`).then((r) => {
      if (!live) return;
      setD(r.data);
      const q = decodeMark(params.get("at"));
      const m = initial.current;
      if (q) apply(q); else if (m && !atStart(m)) setOffer(m);
    }).catch(() => live && setErr(true));
    return () => { live = false; };
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (d && mode === "pages" && d.format !== "webtoon") setPos({ mode: "pages", page: n }); }, [n, mode, d]);
  useEffect(() => {
    if (!pos || (offer && atStart(pos))) return;
    saveMark(slug, pos);
    if (offer) setOffer(null);
  }, [pos, offer, slug]);
  const onPos = useCallback((p) => setPos(p), []);
  const copyLink = async () => { const url = `${window.location.origin}/authors/${slug}/read?at=${encodeMark(pos || { mode: "pages", page: 1 })}`; try { await navigator.clipboard.writeText(url); toast.success("Resume link copied — open it on any device"); } catch { window.prompt("Copy this link", url); } };
  const rtl = d?.direction === "rtl";
  const go = useCallback((dir) => d && setN((x) => Math.min(Math.max(1, x + dir), d.preview_pages)), [d]);
  useEffect(() => {
    const k = (e) => { if (e.key === "ArrowRight") go(rtl ? -1 : 1); if (e.key === "ArrowLeft") go(rtl ? 1 : -1); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [go, rtl]);
  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="reader-error">This preview isn't available.</div>;
  if (!d) return <div className="min-h-screen" />;
  const end = n >= d.preview_pages;
  const scroll = d.format === "webtoon" || mode === "scroll";
  const more = (x) => x.total_pages > x.preview_pages && <p className="text-center text-sm text-amber-700 bg-amber-50 py-6" data-testid="scroll-end">End of the free preview — <Link to={`/authors/${slug}`} className="underline">get the full {x.format}</Link>.</p>;
  return (
    <div className="min-h-screen bg-black" data-testid="public-comic-reader">
      <PublicHeader back />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
          <Link to={`/authors/${slug}`} className="text-sm text-amber-200 inline-flex items-center gap-1" data-testid="reader-back"><ArrowLeft size={14} />{d.title}</Link>
          {d.format !== "webtoon" && <div className="flex gap-1" data-testid="reader-mode">{[["pages", "Page turn"], ["zoom", "Panel zoom"], ["scroll", "Scroll"]].map(([k, l]) => <button key={k} onClick={() => setMode(k)} className={`text-xs px-3 py-1 rounded-full border ${(scroll ? "scroll" : mode) === k ? "border-amber-400 text-amber-200" : "border-slate-700 text-slate-400"}`} data-testid={`reader-mode-${k}`}>{l}</button>)}</div>}
          {!scroll && mode === "pages" && <p className="text-xs text-slate-500" data-testid="reader-position">Page {n} of {d.preview_pages} free{d.total_pages > d.preview_pages ? ` · ${d.total_pages} in the full ${d.format}` : ""}{rtl ? " · reads right to left" : ""}</p>}
          <button onClick={copyLink} className="text-xs inline-flex items-center gap-1 text-slate-400 hover:text-amber-200" data-testid="reader-copy-link"><LinkIcon size={12} />Continue on another device</button>
        </div>
        {offer && (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3" data-testid="resume-banner">
            <BookmarkIcon size={15} className="text-amber-300" /><p className="text-sm text-amber-100 flex-1">Welcome back — pick up from {describeMark(offer)}?</p>
            <button onClick={() => apply(offer)} className="text-xs px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-500 text-white" data-testid="resume-continue">Continue</button>
            <button onClick={() => { clearMark(slug); setOffer(null); }} className="text-xs text-slate-400 hover:text-white" data-testid="resume-start-over">Start over</button>
          </div>
        )}
        {scroll ? <ScrollReader key={`s${gen}`} slug={slug} onEnd={more} start={start?.mode === "scroll" ? start : null} onPos={onPos} /> : mode === "zoom" ? <GuidedReader key={`z${gen}`} slug={slug} start={start?.mode === "zoom" ? start.step : 0} onPos={onPos} /> : d.preview_pages ? (
          <div className="flex items-center gap-2 sm:gap-6 select-none">
            <button onClick={() => go(rtl ? 1 : -1)} className="p-2 text-slate-400 hover:text-white" aria-label="Turn left" data-testid="public-reader-left"><ChevronLeft size={30} /></button>
            <div className="flex-1 flex justify-center" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); const left = e.clientX < r.left + r.width / 2; go(left === rtl ? 1 : -1); }}>
              <img key={n} src={`${API}/public/books/${slug}/comic/pages/${n}`} alt={`Page ${n}`} className="max-h-[80vh] w-auto rounded shadow-[0_0_40px_rgba(157,78,221,0.25)] fade-up" data-testid="public-reader-page" />
            </div>
            <button onClick={() => go(rtl ? -1 : 1)} className="p-2 text-slate-400 hover:text-white" aria-label="Turn right" data-testid="public-reader-right"><ChevronRight size={30} /></button>
          </div>
        ) : <p className="text-center text-slate-400 py-20" data-testid="reader-no-preview">The author hasn't shared a preview yet.</p>}
        {!scroll && mode === "pages" && end && d.total_pages > d.preview_pages && <p className="text-center text-sm text-amber-200 mt-6" data-testid="reader-end">End of the free preview — <Link to={`/authors/${slug}`} className="underline">get the full {d.format}</Link>.</p>}
      </div>
    </div>
  );
}
